import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { DEFAULT_DATE_FORMAT, ONLY_MONTH_DATE_FORMAT } from '@/common/constants';


interface DocGroupItemResponse {
  only_for_model?: string;
  title: string;
  url?: string;
  is_modal?: boolean;
  content?: string;
  date?: string;
  is_line_break?: boolean;
}

interface DocGroupResponse {
  text?: string;
  testId?: string;
  id: string;
  items: DocGroupItemResponse[];
}
interface DocsResponse {
  electronic_disclosure_agreement_url: string;
  HSA_agreement_url: string;
  terms_and_conditions_url: string;
  groups: DocGroupResponse[];
}

export const URL = 'https://docs.elevateaccounts.com/investment-docs/terms_conditions.json';
const QUERY_KEY = 'GET_TERMS_DOCS';
export interface DocsEntity {
  title: string;
  src: string;
  text?: string;
  date?: string;
  lineBreak?: boolean;
  isModal?: boolean;
}
export interface DocsEntityGroup {
  text?: string;
  testId: string;
  id: string;
  docs: DocsEntity[];
}

const useTermsAndConditionsDocsQuery = (currentModel?: string) => {
  const { isLoading, data } = useQuery(
    [QUERY_KEY],
    () => fetch(URL).then((res) => res.json() as unknown as DocsResponse),
    {
      retry: 0,
    }
  );

  const currentData = useMemo(() => {
    const result = data;
    return result;
  }, [data]);
  const formattedDocsGroups = useMemo(() => {
    if (!currentData) {
      return [];
    }
    const groups: DocsEntityGroup[] =
      currentData?.groups?.map((group, index) => {
        return {
          id: `${group.id || index}`,
          testId: group.testId || `doc-group-${index}`,
          text: group.text,
          docs:
            group?.items.reduce((result: DocsEntity[], item) => {
              if (item.only_for_model && item.only_for_model !== currentModel) {
                return result;
              }
              const currentItem: DocsEntity = {
                title: item.title || '',
                src: item.url || '',
                text: item.content,
                lineBreak: item.is_line_break,
                isModal: item.is_modal,
                date: item.date
                  ? dayjs(item.date, DEFAULT_DATE_FORMAT).format(ONLY_MONTH_DATE_FORMAT)
                  : undefined,
              };
              return [...result, currentItem];
            }, []) || [],
        };
      }) || [];
    return groups;
  }, [currentData, currentModel]);
  const formattedDocs = useMemo<DocsEntity[]>(() => {
    return formattedDocsGroups.reduce((result: DocsEntity[], item) => {
      return [...result, ...item.docs];
    }, []);
  }, [formattedDocsGroups]);
  const HSAAgreementLink = useMemo(() => {
    return data?.HSA_agreement_url || '';
  }, [data?.HSA_agreement_url]);
  const termsAndConditionsLink = useMemo(() => {
    return data?.terms_and_conditions_url || '';
  }, [data?.terms_and_conditions_url]);

  const electronicDisclosureAgreementLink = useMemo(() => {
    return data?.electronic_disclosure_agreement_url || '';
  }, [data?.electronic_disclosure_agreement_url]);

  return {
    electronicDisclosureAgreementLink,
    HSAAgreementLink,
    termsAndConditionsLink,
    docsGroups: formattedDocsGroups,
    docs: formattedDocs,
    isLoading: isLoading,
  };
};
export default useTermsAndConditionsDocsQuery;
